import React, { useEffect, useMemo, useRef, useState } from 'react';
import './tracking-window.scss';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../store';
import { FwVersionInfo } from '../../../dtos/version.dto';
import Chevron from '@svg/chevron-up.svg?react';
import CloseX from '@svg/close-grey.svg?react';
import PendingIcon from '@svg/time-clock-circle.svg?react';
import ValidateIcon from '@svg/circle-validate.svg?react';
import ErrorIcon from '@svg/x-circle-outline.svg?react';
import { VersionStatus } from '../../../utils/enums';

const TrackingWindow: React.FC = observer(() => {
    const initialX = window.innerWidth - 356 - 16;
    const initialY = window.innerHeight - 100 - 16;
    const [offset, setOffset] = useState({ x: initialX, y: initialY });
    const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const trackingWindowRef = useRef<HTMLDivElement | null>(null);

    const { fwVersionStore, customerStore } = useStores();

    useEffect(() => {
        const serviceBatchId = fwVersionStore.batchId?.split(':')?.[0];
        if (Number(serviceBatchId) !== customerStore.serviceId) {
            fwVersionStore.setBatchId(null);
        }
    }, [customerStore.serviceId]);

    const connectionList: FwVersionInfo[] = useMemo(() => {
        if (!fwVersionStore.batchId) return [];
        const batchId = fwVersionStore.batchId.split(':')?.[1];
        return Object.values(fwVersionStore.allFwVersions || {})
            .flat()
            .filter((version): version is FwVersionInfo => version?.batchId === batchId);
    }, [fwVersionStore.allFwVersions, fwVersionStore.batchId]);

    const statusCounts = useMemo(() => {
        const statusMapping = {
            PUSH_SENT: 'pending',
            PUSH_OK: 'succeed',
            PUSH_ERROR: 'failed'
        };

        return connectionList.reduce((acc, version) => {
            const status = version.versionStatus as keyof typeof statusMapping;
            const mappedStatus = statusMapping[status];
            acc[mappedStatus] = (acc[mappedStatus] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);
    }, [connectionList]);

    const handleMouseDown = (e: MouseEvent) => {
        e.preventDefault();
        setInitialPosition({
            x: e.clientX - offset.x,
            y: e.clientY - offset.y,
        });
        setIsDragging(true);
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (isDragging && trackingWindowRef.current) {
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;
            const elementWidth = trackingWindowRef.current.offsetWidth;
            const elementHeight = trackingWindowRef.current.offsetHeight;

            const scrollX = window.scrollX;
            const scrollY = window.scrollY;

            let newX = e.clientX - initialPosition.x + scrollX;
            let newY = e.clientY - initialPosition.y + scrollY;

            if (newX < 0) newX = 0;
            if (newY < 0) newY = 0;
            if (newX + elementWidth > windowWidth) newX = windowWidth - elementWidth;
            if (newY + elementHeight > windowHeight) newY = windowHeight - elementHeight;

            setOffset({ x: newX, y: newY });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleClose = () => {
        fwVersionStore.setBatchId(null);
    };

    const toggleExpandCollapse = () => {
        setIsExpanded(prevState => {
            const newState = !prevState;
            if (newState && trackingWindowRef.current) {
                const windowHeight = window.innerHeight;
                const headerHeight = 60;
                const rowHeight = 40;
                const expandedHeight = headerHeight + connectionList.length * rowHeight;
                const currentY = offset.y;

                if (currentY + expandedHeight > windowHeight) {
                    const newY = Math.max(0, windowHeight - expandedHeight - 16);
                    setOffset(prevOffset => ({
                        ...prevOffset,
                        y: newY
                    }));
                }
            }
            return newState;
        });
    };

    useEffect(() => {
        const handleResize = () => {
            if (trackingWindowRef.current) {
                const windowWidth = window.innerWidth;
                const windowHeight = window.innerHeight;
                const elementWidth = trackingWindowRef.current.offsetWidth;
                const elementHeight = trackingWindowRef.current.offsetHeight;

                const newX = windowWidth - elementWidth - 16;
                const newY = windowHeight - elementHeight - 16;

                setOffset({x: newX, y: newY});
            }
        };

        window.addEventListener('resize', handleResize);

        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        } else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    const isWindowVisible = connectionList.length > 0;

    // Native listener: dragging is a pointer-only enhancement, not a keyboard-operable control.
    useEffect(() => {
        const element = trackingWindowRef.current;
        element?.addEventListener('mousedown', handleMouseDown);
        return () => element?.removeEventListener('mousedown', handleMouseDown);
    }, [offset, isWindowVisible]);

    const renderSummary = () => {
        return Object.entries(statusCounts)
            .map(([status, count]) => `${count} ${status}`);
    };

    const getServiceNameBySid = (sid: number)=>  {
        const service = customerStore.getCustomerServiceData(sid);
        return service?.serviceName || service?.adiCircuitId;
    };

    if (connectionList.length === 0) {
        return null;
    }

    return (
        <div
            ref={trackingWindowRef}
            className="tracking-window"
            style={{ transform: `translate3d(${offset.x}px, ${offset.y}px, 0)` }}
        >
            <div className="tracking-window-header">
                <span>
                    {statusCounts['pending'] > 0 ? `Applying policy to ${connectionList.length} connections` : `Applying policy completed (${connectionList.length})`}
                </span>
                <div className="tracking-window-header-actions">
                    <Chevron className={`chevron ${isExpanded ? 'expand' : 'collapse'}`} onClick={toggleExpandCollapse}/>
                    <CloseX className="close" onClick={handleClose} />
                </div>
            </div>
            <div className={`tracking-window-content ${isExpanded ? 'expanded' : 'collapse'}`}>
                {isExpanded ? (
                    connectionList.map(version => (
                        <div key={version.externalId} className="connection-row">
                            <span>{getServiceNameBySid(version.sid)}</span>
                            <span className="connection-status">
                                {version.versionStatus === VersionStatus.PUSH_SENT && <PendingIcon className="pending" />}
                                {version.versionStatus === VersionStatus.PUSH_OK && <ValidateIcon className="success" />}
                                {version.versionStatus === VersionStatus.PUSH_ERROR && <ErrorIcon className="error" />}
                            </span>
                        </div>
                    ))
                ) : (
                    <div className="summary">{renderSummary().map(item =>
                        <span className={`status-item ${item}`} key={item}>{item}</span>)}
                    </div>
                )}
            </div>
        </div>
    );
});

export default TrackingWindow;
